import React, { useState, useEffect } from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/* Theme  : same as Our Values.
   Colours: headings #4573a6 | buttons #2596be | line #c9dde8
   Icon   : white arrow on a gradient circle, so it is always visible on hover. */

const SIZE = 56;
const STROKE = 3;
const R = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * R;

const STYLES = `
.stt{--head:#4573a6;--btn:#2596be;--line:#c9dde8;
  position:fixed;right:30px;bottom:30px;z-index:1000;width:${SIZE}px;height:${SIZE}px;padding:0;border:none;border-radius:50%;cursor:pointer;
  background:#fff;box-shadow:0 18px 30px -14px rgba(69,115,166,.8);
  opacity:0;transform:translateY(30px) scale(.8);pointer-events:none;
  transition:opacity .4s,transform .5s cubic-bezier(.3,1.5,.5,1),box-shadow .35s}
.stt.show{opacity:1;transform:none;pointer-events:auto}
.stt.show:hover{transform:translateY(-6px);box-shadow:0 26px 36px -14px rgba(37,150,190,.9)}

.stt svg.ring{position:absolute;inset:0;width:100%;height:100%;transform:rotate(-90deg)}
.stt .track{fill:none;stroke:var(--line);stroke-width:${STROKE}}
.stt .fill{fill:none;stroke:url(#sttGrad);stroke-width:${STROKE};stroke-linecap:round;stroke-dasharray:${CIRC};transition:stroke-dashoffset .12s linear}

.stt .core{position:absolute;inset:6px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;
  background:linear-gradient(135deg,var(--head),var(--btn));transition:background .35s}
.stt:hover .core{background:linear-gradient(135deg,var(--btn),var(--head))}
.stt .core svg{transition:transform .35s cubic-bezier(.3,1.6,.5,1)}
.stt:hover .core svg{animation:sttBounce .8s ease infinite}

/* label slides out to the left */
.stt .lab{position:absolute;right:calc(100% + 12px);top:50%;transform:translate(10px,-50%);padding:7px 14px;border-radius:50px;
  background:var(--head);color:#fff;font-family:"Open Sans",sans-serif;font-size:12px;letter-spacing:1px;text-transform:uppercase;white-space:nowrap;
  opacity:0;pointer-events:none;box-shadow:0 12px 20px -10px var(--head);transition:opacity .3s,transform .4s cubic-bezier(.3,1.4,.5,1)}
.stt:hover .lab{opacity:1;transform:translate(0,-50%)}

@keyframes sttBounce{50%{transform:translateY(-4px)}}

@media(max-width:600px){.stt{right:16px;bottom:16px}.stt .lab{display:none}}
@media(prefers-reduced-motion:reduce){.stt,.stt *{animation:none!important;transition:none!important}}
`;

const ScrollToTopButton: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(window.scrollY / max, 1) : 0);
      setVisible(window.scrollY > 300);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <style>{STYLES}</style>

      <button
        className={"stt" + (visible ? " show" : "")}
        onClick={scrollToTop}
        aria-label="Scroll to top"
        tabIndex={visible ? 0 : -1}
      >
        <svg className="ring" viewBox={`0 0 ${SIZE} ${SIZE}`}>
          <defs>
            <linearGradient id="sttGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#4573a6" />
              <stop offset="100%" stopColor="#2596be" />
            </linearGradient>
          </defs>
          <circle className="track" cx={SIZE / 2} cy={SIZE / 2} r={R} />
          <circle
            className="fill"
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            style={{ strokeDashoffset: CIRC * (1 - progress) }}
          />
        </svg>

        <span className="core">
          <KeyboardArrowUpIcon style={{ fontSize: 30, color: "#fff" }} />
        </span>

        <span className="lab">Top</span>
      </button>
    </>
  );
};

export default ScrollToTopButton;